import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { FileText, Sparkles, Download, Copy } from "lucide-react";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";

const selectCls = "w-full px-3 py-2 text-sm border rounded-lg bg-background border-border";

const ClinicalSynthesis = ({ patientId, reportsCount }) => {
  const [synthesis, setSynthesis] = useState(null);
  const [models, setModels] = useState([]);
  const [vertexHint, setVertexHint] = useState(null);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [instructions, setInstructions] = useState("");
  const [version, setVersion] = useState("nominative");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/patients/${patientId}/synthesis`, { withCredentials: true }).then((r) => setSynthesis(r.data.exists === false ? null : r.data)).catch(() => {});
    axios.get(`${API}/followup/options`, { withCredentials: true }).then((r) => { setModels(r.data.models); setVertexHint(r.data.forced_model ? `HDS : génération via Vertex AI (${r.data.vertex?.location}, UE) — ${r.data.forced_model}` : null); }).catch(() => {});
  }, [patientId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/patients/${patientId}/synthesis`, { model, instructions }, { withCredentials: true });
      setSynthesis(r.data); toast.success("Synthèse clinique générée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const download = async (v) => {
    try {
      const r = await axios.get(`${API}/patients/${patientId}/synthesis/docx?version=${v}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `synthese-clinique-${v}.docx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors du téléchargement"); }
  };

  const content = synthesis ? (version === "anonyme" ? synthesis.content_anonymous : synthesis.content) : "";

  return (
    <Card data-testid="clinical-synthesis-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2"><FileText className="w-5 h-5" /> Synthèse clinique consolidée</CardTitle>
        <p className="text-sm text-muted-foreground">Générée à partir de tous les comptes rendus ({reportsCount}) : version nominative pour le dossier, version anonymisée pour un cas clinique ou un article.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-[240px_1fr] gap-3">
          <div><Label className="text-xs">Modèle IA</Label>
            <select className={selectCls} value={model} onChange={(e) => setModel(e.target.value)} data-testid="synthesis-model-select">
              {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            {vertexHint && <p className="text-[11px] text-emerald-700 mt-1" data-testid="synthesis-vertex-hint">{vertexHint}</p>}</div>
          <div><Label className="text-xs">Instructions spécifiques (optionnel)</Label>
            <Textarea rows={1} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Ex. Insister sur l'évolution de l'anxiété sociale" data-testid="synthesis-instructions" /></div>
        </div>
        <Button onClick={generate} disabled={generating || reportsCount === 0} className="gap-2" data-testid="synthesis-generate-btn">
          <Sparkles className="w-4 h-4" /> {generating ? "Génération en cours (1 à 2 min)..." : synthesis ? "Régénérer la synthèse" : "Générer la synthèse"}
        </Button>

        {synthesis && (
          <div className="space-y-3" data-testid="synthesis-result">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1 bg-muted/50 rounded-lg p-1">
                {[["nominative", "Nominative"], ["anonyme", "Anonymisée"]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setVersion(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${version === v ? "bg-background shadow font-medium" : "text-muted-foreground"}`} data-testid={`synthesis-tab-${v}`}>{label}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(content); toast.success("Copié"); }} className="gap-1.5" data-testid="synthesis-copy-btn"><Copy className="w-3.5 h-3.5" /> Copier</Button>
                <Button variant="outline" size="sm" onClick={() => download(version)} className="gap-1.5" data-testid="synthesis-download-btn"><Download className="w-3.5 h-3.5" /> Word (.docx)</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Générée le {formatDateSafe(synthesis.generated_at)} · {synthesis.sessions_count} séance(s) · {synthesis.model_used}</p>
            <div className="prose prose-sm dark:prose-invert max-w-none border rounded-lg p-4 bg-muted/20" data-testid="synthesis-content"><ReactMarkdown>{content}</ReactMarkdown></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClinicalSynthesis;
